import React from 'react';
import classnames from 'classnames';
import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';
import LoadingIndicator from '@wix/da-shared-react/pkg/LoadingIndicator';
import { WithSectionContextProps } from '../../Sections/SectionContext';
import { ModuleType } from '../../../../types/modules';
import { ZoneType } from '../../../../types/layout';
import { hashString } from '../../../utils/hash';
import AddWidget from '../AddWidgetV2';
import s from './Zone.scss';

export interface Props extends WithSectionContextProps {
  type: ZoneType;
  locked?: boolean;
  isRemovable?: boolean;
  isCurrentUserProfileOwner: boolean;
  isMobile: boolean;
  installableModuleTypes: ModuleType[];
  installedModuleIds: number[];
  installedComponents: {
    moduleId: number;
    ComponentClass: React.ComponentType<any>;
  }[];
  mapModuleTypeToWidgetComponent: {
    [moduleName: string]: React.ComponentType<any>;
  };
  isInstallingModule?: boolean;
  currentlyInstalledModulePostion?: number;
  className?: string;
  moveModule: (moduleId: number, position: number) => void;
  removeModule: (moduleId: number) => void;
  addModule: (moduleType: ModuleType, position: number) => void;
  children?: React.ReactNode;
}

const defaultProps = {
  installableModuleTypes: [],
  installedModuleIds: [],
  locked: false,
  isRemovable: true,
};

class Zone extends React.PureComponent<Props> {
  static defaultProps = defaultProps;
  render() {
    const {
      section,
      type: zone,
      installedComponents,
      moveModule,
      removeModule,
      isRemovable,
      locked,
      isInstallingModule,
      currentlyInstalledModulePostion,
      isCurrentUserProfileOwner,
    } = this.props;

    return (
      <div
        className={classnames(s['root'], this.props.className)}
        key={this.props.type}
      >
        {installedComponents.map((installedComponent, idx, modules) => {
          const { moduleId, ComponentClass } = installedComponent;
          const isFirst = idx === 0;
          const isLast = idx === modules.length - 1;
          const canMoveUp = !isFirst;
          const canMoveDown = !isLast;
          const props = {
            key: idx,
            moduleId,
            widgetMoveUp: !locked && canMoveUp && moveModule(moduleId, idx - 1),
            widgetMoveDown:
              !locked && canMoveDown && moveModule(moduleId, idx + 1),
            widgetRemove: !locked && isRemovable && removeModule(moduleId),
            isCurrentUserProfileOwner,
            section,
            zone,
          };
          return (
            <ErrorBoundary key={idx}>
              {isInstallingModule &&
                idx === currentlyInstalledModulePostion &&
                this.renderLoadingIndicator()}
              <ComponentClass {...props} />
              {!isLast && this.renderAddWidgetIfAllowed(idx + 1)}
            </ErrorBoundary>
          );
        })}
      </div>
    );
  }

  renderLoadingIndicator() {
    return <LoadingIndicator view="daSymbolPurple" />;
  }

  renderAddWidgetIfAllowed(position) {
    const {
      type,
      locked,
      isInstallingModule,
      installableModuleTypes,
      isCurrentUserProfileOwner,
      isMobile,
      addModule,
      section,
    } = this.props;

    if (
      locked ||
      isMobile ||
      !isCurrentUserProfileOwner ||
      !installableModuleTypes ||
      !installableModuleTypes.length
    ) {
      return null;
    }
    const installableModules = installableModuleTypes.map(moduleName => ({
      zone: type,
      moduleName,
      position,
    }));

    const uniqueKey = hashString(
      installableModules
        .reduce(
          (result, { zone, moduleName, position: pos }) => [
            ...result,
            `${zone}.${moduleName}.${pos}`,
          ],
          []
        )
        .join(':')
    );

    return (
      <AddWidget
        section={section}
        key={`add-widget-${uniqueKey}`}
        className={s['add-widget']}
        addModule={(zone, moduleName, modulePosition) =>
          addModule(moduleName, modulePosition)
        }
        installableModules={installableModules}
        disabled={isInstallingModule}
      />
    );
  }
}

export default Zone;
